/**
 * 中判フィルムフォーマットの実寸 (mm) と、35mm 換算ヘルパー
 *
 * 各フィルムの実画面サイズは実機で若干異なる (例: Pentax 67 は 56×69.5)。
 * 一般値を採用し、画角計算で 1度未満の差は誤差として許容。
 */

export const FILM_DIMENSIONS_MM = {
  '645': { w: 56,  h: 41.5, label: '6×4.5' },
  '66':  { w: 56,  h: 56,   label: '6×6'   },
  '67':  { w: 70,  h: 56,   label: '6×7'   },
  '68':  { w: 76,  h: 56,   label: '6×8'   },
  '69':  { w: 84,  h: 56,   label: '6×9'   },
  '617': { w: 168, h: 56,   label: '6×17'  },
};

export const FORMAT_35MM = { w: 36, h: 24 };

/**
 * 35mm 判 → 中判の crop factor (対角基準)
 * 例: 67 → 約 0.484 → 「中判 100mm = 35mm換算 48mm」
 */
export function getCropFactor(filmFormat) {
  const film = FILM_DIMENSIONS_MM[filmFormat];
  if (!film) return 1.0;
  const filmDiag = Math.hypot(film.w, film.h);
  const baseDiag = Math.hypot(FORMAT_35MM.w, FORMAT_35MM.h);
  return baseDiag / filmDiag;
}

/**
 * 中判レンズ焦点距離 → 35mm 換算焦点距離
 */
export function get35mmEquivFocal(filmFormat, focalMm) {
  if (!Number.isFinite(focalMm) || focalMm <= 0) return null;
  return focalMm * getCropFactor(filmFormat);
}

/**
 * 水平画角 (degree)
 *   FOV_h = 2 * atan(width / (2 * focal))
 */
export function getHorizontalFovDegrees(filmFormat, focalMm) {
  const film = FILM_DIMENSIONS_MM[filmFormat];
  if (!film || !focalMm) return null;
  return 2 * Math.atan(film.w / (2 * focalMm)) * (180 / Math.PI);
}

/**
 * iPhone のメインカメラ基準画角 (35mm 換算)
 * - iPhone 13/14/15/16 系の wide camera は 24mm 相当
 * - 一部機種 (SE, 古い iPad 等) は 26mm や 28mm の場合あり
 * - SETTINGS で上書き可能
 */
export const IPHONE_BASE_FOV_35MM_DEFAULT = 24;

const BASE_FOV_KEY = 'zn.settings.deviceBaseFov';
export function getDeviceBaseFov() {
  const v = parseFloat(localStorage.getItem(BASE_FOV_KEY));
  return Number.isFinite(v) && v > 5 ? v : IPHONE_BASE_FOV_35MM_DEFAULT;
}
export function setDeviceBaseFov(mm) {
  if (Number.isFinite(mm) && mm > 5 && mm < 60) {
    localStorage.setItem(BASE_FOV_KEY, String(mm));
  }
}

/**
 * 与えられたプリセットから、デバイスで再現すべきズーム倍率を計算
 * 1.0 未満は 1.0 にクランプ (デジタルズームは縮小不可)
 */
export function calculateZoomFromPreset(preset, baseFov35mm) {
  const focalMm = preset?.defaultLensFocalMm;
  if (!focalMm || focalMm <= 0 || !preset?.filmFormat) return 1.0;
  const equiv = get35mmEquivFocal(preset.filmFormat, focalMm);
  if (!equiv) return 1.0;
  const base = baseFov35mm || getDeviceBaseFov();
  const zoom = equiv / base;
  return Math.max(1.0, Math.min(20, zoom));
}

/**
 * "SMC Takumar 105/2.4" などの文字列から焦点距離 (mm) を抽出
 */
export function parseFocalFromLensName(lensName) {
  if (!lensName || typeof lensName !== 'string') return null;
  // "105mm", "105/2.4", "80 mm", "1:2.8 f=80mm", "300mm f/4" などに対応
  const m = lensName.match(/(\d{2,4})\s*(?:mm|\/|\s)/i);
  if (m) {
    const v = parseInt(m[1], 10);
    if (v >= 20 && v <= 2000) return v;
  }
  return null;
}
